"use client";

// Write-through helper + pending-write guard for the in-memory stores.
//
// The stores stay synchronous and authoritative for render; this module
// durably mirrors each write to the server (/api/store) and remembers the
// value we last wrote per key so a background poll can't revert the acting
// user's own change before the server echoes it back.
//
// ─── AND IT IS WHERE A REFUSED WRITE BECOMES SOMETHING THE USER CAN SEE ─────
//
// Every mutation in the app runs through persist(): stage moves, script text,
// comments, drive videos, brand guides, schedules. That makes this the one place
// worth teaching about failure, and for a long time it was also the one place
// that quietly didn't — a refused write logged a console line and the screen
// carried on showing a change that existed nowhere but this tab.
//
// It now files the failure with lib/write-failure.ts, which beacons the whole
// thing to /api/client-error, puts a notice on screen saying the work is safe,
// and offers one click to send it again. The reference the report is filed under
// is the SERVER's business — the notice deliberately does not ask anyone to copy
// it down. Nothing here decides what the UI does about any of that; it decides
// only that the UI gets told.

import { getWriteFailures, recordWriteFailure, resolveWriteFailure } from "./write-failure";
import { resyncStoreNext } from "./store-cursor";
import { getScriptConflict, pauseScriptSaves } from "./script-conflict";
import { isWorkflowEvent } from "./analytics-schema";
import { trackSaveFailure, trackWorkflow } from "./product-analytics";

// Per-key "value I last wrote locally, not yet confirmed by the server."
// key = `${kind}:${id}`. Consulted by each store's applyServerRows.
const pending = new Map<string, string>();  // key -> JSON of the written value

// ─── AND WHEN THE SERVER'S ANSWER IS NEWER THAN OURS ───────────────────────
//
// The guard waits for the server to echo our exact value. That is right for a
// poll whose read may have come BEFORE our write landed: its row is older than
// ours, and taking it would revert the screen. It is wrong once our write has
// landed and the poll was asked after that: the row it brings is ours or
// NEWER, and when it is newer (another tab wrote the same key after us) the
// echo we wait for will never come. The diff sends each change once, so the
// key then accepted no server update again: a script edited in two tabs, the
// first tab hidden while the second typed, never heard of the second's text,
// and its next keystroke put its own older text back.
//
// So each write the server answers ok is stamped with a count (`acks`), and a
// poll notes the count when it is ASKED (pollMark). While its answer is
// applied (applyingPoll), a key whose write landed at or before that mark
// takes the server's row, echo or not. A poll asked before the write landed
// still waits for the echo, as before. A refused write never lands, so it
// stays pending as it always has.
const landedAt = new Map<string, number>(); // key -> `acks` when its write was answered ok
let acks = 0;
let applyingSince: number | null = null;

/** The count a poll notes as it is asked: every write answered by now is in its answer. */
export function pollMark(): number {
  return acks;
}

/** Apply a poll's answer, asked at `mark`, so the guard can trust what landed before it. */
export function applyingPoll<T>(mark: number, apply: () => T): T {
  const was = applyingSince;
  applyingSince = mark;
  try {
    return apply();
  } finally {
    applyingSince = was;
  }
}

export function isConfirmed(kind: string, id: string, serverData: unknown): boolean {
  const key = `${kind}:${id}`;
  if (kind === 'draft' && getScriptConflict(id)) {
    pending.delete(key);
    landedAt.delete(key);
    return true; // local editor fields and recovery storage retain unsaved text
  }
  const want = pending.get(key);
  if (want === undefined) return true;                 // nothing pending → accept server
  const landed = landedAt.get(key);
  if (
    want === JSON.stringify(serverData ?? null) ||     // server echoed our write → confirmed
    (landed !== undefined && applyingSince !== null && landed <= applyingSince) // asked after it landed → ours or newer
  ) {
    pending.delete(key);
    landedAt.delete(key);
    return true;
  }
  return false;                                        // our write not yet reflected → skip
}

// A mutation must NOT block on the network — the in-memory Map is already
// authoritative for render; this durably mirrors it and marks the key pending.
//
// It returns the write anyway, for the one caller that cannot proceed without
// it: anything that then asks the SERVER to read the row it just wrote. Grading
// is exactly that — /api/grade carries a scriptId and the server reads the
// fields out of the database, so a fire-and-forget draft write racing the grade
// POST means the model scores the text you had a moment ago. Awaiting is that
// caller's business; everyone else keeps ignoring this and stays instant.
// A fetch is bound to the document that started it: a reload or a closed tab
// ABORTS one still in flight, and fire-and-forget means nobody notices. That is
// a real lost edit — type a title, hit reload, and the POST that was carrying it
// dies with the page — and it is the same race that made the new-week e2e spec
// flaky, where the next test's page.goto tore down the document mid-write.
//
// `keepalive` is the browser's promise to finish the request after the page is
// gone. The spec caps the TOTAL body of all in-flight keepalive requests at 64KB
// and REJECTS the fetch above it — so a big row would trade a lost write for a
// failed one. Anything near the cap therefore goes as a normal fetch: it keeps
// its full-size write while the page lives, which is every case except the
// unload we cannot help it with anyway.
const KEEPALIVE_MAX_BYTES = 60_000; // under 64KB, with room for the other rows in flight

// A REFUSED WRITE IS NOT A WRITE, and fetch() will not tell you the difference.
// It rejects only when the request never completes; a 401, a 413 or a 500
// RESOLVES, so a bare `.then()` records a write the server threw away as
// "durably mirrored". That is the read path's old bug, which store-sync.tsx was
// hardened against — "A permanently-failing poll used to be invisible: the app
// rendered fine and simply never synced" — left standing on the write side,
// where it costs more: a poll that never runs shows stale data, a write that
// never lands loses work.
//
// The console line stays, because it is what you read while watching a deploy.
// It is no longer the WHOLE response: the failure is also filed, which is what
// puts it in front of the person it happened to and into the server log.
function refused(kind: string, id: string, why: string): void {
  console.error(`[store-persist] ${kind}:${id} did not save — ${why}. This client is now showing text the server does not have.`);
}

/**
 * File a failed write — unless a NEWER write for the same row has already
 * replaced it.
 *
 * THE SUPERSEDED CHECK IS NOT AN OPTIMISATION, IT IS A CORRECTNESS RULE. A draft
 * autosaves every 800ms, so two writes for one document can be in the air at
 * once and they are not guaranteed to answer in order. If the newer one succeeds
 * and the older one then fails, filing the older one would put a retry button on
 * screen whose click re-POSTs text the author has already typed over — an "undo"
 * disguised as a repair, which is worse than the failure it was answering.
 *
 * `pending` already holds the JSON of the last value written for this key, so
 * the test is just "is what I sent still what we mean to say". `value` is null
 * for a DELETE, which drops its pending entry rather than setting one and so has
 * nothing to be superseded by.
 */
function fileFailure(
  kind: string,
  id: string,
  op: "write" | "delete",
  value: string | null,
  status: number | undefined,
  retry: () => Promise<void>,
): void {
  const key = `${kind}:${id}`;
  if (value !== null && pending.get(key) !== value) return; // superseded — the newer write owns this row
  trackSaveFailure(status);
  recordWriteFailure({
    key,
    kind,
    id,
    op,
    reason: status === undefined ? "unreachable" : "refused",
    ...(status === undefined ? {} : { status }),
    retry,
  });
}

// Writes still in the air. A mutation must not BLOCK on the network, but one
// caller has to know when the network has caught up: the submit notification
// asks the SERVER to read the stages the client has just written, and a
// fire-and-forget POST that loses the race means an AM is told about a week that
// still looks unsubmitted from the database's side. Same hazard the grade path
// has, answered once for everyone instead of by threading a promise through
// three call sites. (Grading awaits persist() directly — it needs its own row,
// not the quiet.)
// Promise<unknown>: writeRow resolves a boolean and forget() a void, and this
// set only ever asks "is it finished".
const inflight = new Set<Promise<unknown>>();
// Keep this tab's draft revisions in order, including initial slot creation.
const draftWrites = new Map<string, Promise<unknown>>();

/** Resolves when every write started before this call has landed (or failed). */
export function whenPersisted(): Promise<void> {
  return Promise.all(Array.from(inflight)).then(() => undefined);
}

// DELETEs in the air, counted apart from `inflight`: whenPersisted() has always
// waited for writes only, and its callers ask the server about rows written,
// not rows removed. writesSettled() needs both.
let deletesInFlight = 0;

/**
 * Could this tab load a new document now without losing a write? (A stale
 * tab's next navigation is a document load, unless this says no,
 * lib/stale-tab.ts.)
 *
 * Yes only when nothing is in the air — a write or a delete — and no refused
 * one is waiting in the failure notice, whose value and retry live only in
 * this tab.
 *
 * ⚠️ NOT "the pending-write guard is empty". `pending` keeps a key from the
 * write until the poll ECHOES it, and a hidden tab does not poll, so
 * asking that would keep every tab that ever saved and was then hidden on its
 * old client until someone looked at it. A write the server answered ok is on
 * the server; the new document reads it back from there. A refused one stays
 * pending too, but it is also filed, and the failure queue is what says so.
 */
export function writesSettled(): boolean {
  return inflight.size === 0 && deletesInFlight === 0 && getWriteFailures().length === 0;
}

/**
 * The write itself, and the ONE place that knows whether it landed.
 *
 * Split out of persist() so a caller can act on the answer. persist() has always
 * resolved the same way for a 200 and a 401 — deliberately, since a mutation
 * must not block on the network and nobody was going to branch on it — but the
 * moment a write became half of a two-step operation (relocate, below) somebody
 * had to be able to ask.
 *
 * `retry` is a parameter rather than a closure over persist() for the same
 * reason: what "send it again" means depends on what the write was PART of. A
 * relocated row's retry has to redo the move, not just the write, or a retry
 * that finally lands leaves the old key behind forever.
 */
function writeRow(
  kind: string,
  id: string,
  data: unknown,
  retry: () => Promise<void>,
  scheduleFrom?: string,
  options?: ConfirmedWrite,
): Promise<boolean> {
  const key = `${kind}:${id}`;
  const value = JSON.stringify(data ?? null);
  pending.set(key, value);
  landedAt.delete(key); // this write has not landed, whatever an earlier one did
  const body = JSON.stringify({ kind, id, data, ...(scheduleFrom ? { scheduleFrom } : {}),
    ...(options?.expectedTextVersion !== undefined ? { expectedTextVersion: options.expectedTextVersion } : {}),
    ...(options?.textOnly ? { textOnly: true } : {}),
  });
  const again = retry;

  const send = () => {
    if (kind === 'draft' && getScriptConflict(id)) return Promise.resolve(false);
    return fetch('/api/store', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: body.length <= KEEPALIVE_MAX_BYTES,
  })
    .then(async (res) => {
      if (res.ok) {
        const result = typeof res.json !== 'function' ? null : options?.textOnly
          ? await res.json()
          : await Promise.resolve().then(() => res.json()).catch(() => null);
        // Landed: a poll asked from now on carries it, or something newer. Only
        // while it is still the value we mean, so a newer write in the air keeps
        // the guard for itself.
        if (pending.get(key) === value) landedAt.set(key, ++acks);
        // A row that failed a minute ago and has now landed is no longer a
        // failure, whether this call was the retry or just the next autosave.
        // Clearing here rather than in the retry button means the queue is true
        // even when the thing that fixed it was not a click.
        resolveWriteFailure(key);
        if (result?.draft) options?.onDraftSaved?.(result.draft);
        if (isWorkflowEvent(result?.analytics)) trackWorkflow("completed", id, result.analytics);
        return true;
      }
      if (kind === 'draft' && res.status === 409) {
        const result = await res.json().catch(() => null);
        if (result?.code === 'script_conflict') {
          pending.delete(key);
          landedAt.delete(key);
          resolveWriteFailure(key);
          pauseScriptSaves(id, result.current ?? null);
          trackSaveFailure(409);
          return false;
        }
      }
      refused(kind, id, `/api/store responded ${res.status}`);
      fileFailure(kind, id, 'write', value, res.status, again);
      return false;
    })
    .catch(() => {
      refused(kind, id, 'the request never completed');
      fileFailure(kind, id, 'write', value, undefined, again);
      return false;
    });
  };
  const preceding = kind === 'draft' ? draftWrites.get(id) : undefined;
  const write = (preceding ? preceding.then(send) : send()).finally(() => {
    inflight.delete(write);
    if (draftWrites.get(id) === write) draftWrites.delete(id);
  });
  if (kind === 'draft') draftWrites.set(id, write);
  inflight.add(write);
  return write;
  // THE KEY STAYS PENDING, ON PURPOSE. A refused write will never be echoed —
  // the server has nothing to echo — so isConfirmed() answers false for this key
  // until the value finally lands, and the client accepts no server update for
  // that row in the meantime.
  //
  // Dropping the key instead would let the next poll (≤10s) revert the row to the
  // server's version — deleting, on screen, the words the author is still typing,
  // and taking with them the only copy of the thing a retry would have sent.
  //
  // That used to be a trade with a bad half: the row was blinded to the server
  // AND nobody was told, so a session that expired at 2pm looked perfectly normal
  // until the tab was closed. The blinding is now the deliberate half of a pair —
  // we hold the user's value, and we say out loud that we are holding it. See the
  // note at the top of lib/write-failure.ts for why the value is never thrown
  // away to buy consistency back.
}

type ConfirmedWrite = {
  confirmed: true;
  expectedTextVersion?: number;
  textOnly?: true;
  onDraftSaved?: (entry: unknown) => void;
  /** Optimistic rows written by the same server transaction, not extra POSTs. */
  mirrors?: { kind: string; id: string; data: unknown }[];
};

export function persist(kind: string, id: string, data: unknown): Promise<void>;
export function persist(kind: string, id: string, data: unknown, options: ConfirmedWrite): Promise<boolean>;
export function persist(kind: string, id: string, data: unknown, options?: ConfirmedWrite): Promise<void | boolean> {
  // The retry the notice's one button runs: this same call, with this same
  // value. It resolves whether or not it worked, because whichever way it goes
  // is already recorded — a success clears the row, a second failure re-files it
  // — and a rejecting retry would only give the notice a second failure mode to
  // render.
  const again = () => options
    ? persist(kind, id, data, options).then(() => undefined)
    : persist(kind, id, data);
  const mirrors = (options?.mirrors ?? []).map((row) => ({
    key: `${row.kind}:${row.id}`, value: JSON.stringify(row.data ?? null),
  }));
  for (const mirror of mirrors) {
    pending.set(mirror.key, mirror.value);
    landedAt.delete(mirror.key);
  }
  // Most callers keep the original fire-and-forget contract. A dependent
  // operation (reschedule, THEN rename) must distinguish failure from success.
  const written = writeRow(kind, id, data, again, undefined, options).then((landed) => {
    if (landed) for (const mirror of mirrors) {
      if (pending.get(mirror.key) === mirror.value) landedAt.set(mirror.key, ++acks);
    }
    return landed;
  });
  return options?.confirmed ? written : written.then(() => undefined);
}

/**
 * MOVE a row from one key to another: write the new one, and delete the old one
 * ONLY once the new one has landed.
 *
 * The order is the whole point, and getting it wrong destroys data. Re-keying
 * used to be a persist() and a forget() fired side by side, neither waiting on
 * the other — so a server that refused the write while accepting the delete left
 * the row written nowhere and deleted from the one place it had been. The week
 * whose post date that was showed the date on screen, the notice said "nothing
 * has been thrown away", and a reload proved otherwise. A QA walk found it on
 * the rename path; it had been true of the brand-move path since re-keying
 * existed.
 *
 * SO A FAILED WRITE NOW LEAVES THE OLD ROW ALONE. The two sides disagree —
 * client at the new key, server still at the old one — which is exactly the
 * disagreement the failure notice exists to announce, and it is recoverable in
 * the direction that keeps the data. The alternative was unrecoverable in the
 * direction that loses it.
 *
 * And the retry redoes the MOVE, not just the write. A retry that re-sent only
 * the new row would land it while leaving the old one behind forever — the stale
 * key that a later week of that number silently inherits, which is the Week-18
 * shape this whole re-key discipline is built against.
 */
export function relocate(kind: string, fromId: string, toId: string, data: unknown): Promise<void> {
  const again = () => relocate(kind, fromId, toId, data);
  const from = fromId.split('::');
  const to = toId.split('::');
  const samePair = from.length === 3 && to.length === 3 && from[0] === to[0] && from[1] === to[1];
  const scheduleFrom = samePair && fromId !== toId && (kind === 'week_date' || kind === 'week_plan') ? fromId : undefined;
  return writeRow(kind, toId, data, again, scheduleFrom).then((landed) => {
    if (landed) return forget(kind, fromId);
  });
}

/**
 * Delete a row outright — the other half of MOVING one from one key to another.
 *
 * There was no delete in this app until a week could change brands, because
 * nothing keyed on anything that moved. A week's post date and plan are keyed by
 * creator+brand+week, so reassigning the brand rewrites the key: the row is
 * written under the new one and the old one has to go, or it sits in the
 * database as a set of dates belonging to a week that no longer exists there —
 * and a NEW week later created under that same brand and number silently
 * inherits them. That is the Week-18 failure with a different first step.
 *
 * The pending guard is DROPPED rather than set. Nothing will ever echo a deleted
 * row, so a pending entry for this key could only ever mean "reject the server's
 * copy forever". Dropping it means that if the DELETE fails, the next poll
 * legitimately brings the row back — which is the truth: it is still there.
 * Since the poll became a diff it brings back only what
 * CHANGED, and a refused DELETE changed nothing, so a failure asks the next
 * poll for the whole store (resyncStoreNext).
 *
 * WHICH IS ALSO WHY A FAILED DELETE STILL HAS TO BE SAID OUT LOUD. This is the
 * one path where the client DOES converge back on the server by itself, and
 * converging quietly is its own kind of lie: a week reassigned to another brand
 * leaves its old date row behind, the poll puts that date back on screen ten
 * seconds later, and the admin who moved it sees their change undo itself with
 * no explanation. The notice is the explanation.
 */
export function forget(kind: string, id: string): Promise<void> {
  const key = `${kind}:${id}`;
  pending.delete(key);
  landedAt.delete(key);
  const q = `kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`;
  const again = () => forget(kind, id).then(() => undefined);
  // Query string, not a body: a DELETE with a body is legal and inconsistently
  // handled by proxies, and both values are short ids rather than payloads.
  deletesInFlight += 1;
  return fetch(`/api/store?${q}`, { method: 'DELETE', keepalive: true })
    .then((res) => {
      if (res.ok) {
        resolveWriteFailure(key);
        return;
      }
      refused(kind, id, `DELETE /api/store responded ${res.status}`);
      fileFailure(kind, id, 'delete', null, res.status, again);
      resyncStoreNext();
    })
    .catch(() => {
      refused(kind, id, 'the request never completed');
      fileFailure(kind, id, 'delete', null, undefined, again);
      resyncStoreNext();
    })
    // Last, so a refused DELETE is filed before it stops counting as in the air.
    .finally(() => { deletesInFlight -= 1; });
}
